// Local-first picker session.
//
// When the warehouse has `picking_local_first_enabled` on, the FE owns
// scan state for the duration of the wave. `/picking/active` ships the
// full wave (every line + a barcode→SKU map + the tote registry) on
// bootstrap; subsequent scans validate locally against that state for
// instant feedback. The actual writes still go to the BE — they're
// fired in the background via a sync queue and the FE doesn't block
// on the response. On any divergence (wave_version drift, server
// rejection, force-release) the hook re-fetches /picking/active and
// rebuilds local state.
//
// The hook exposes a synchronous `processScan(barcode)` returning a
// `ScanResult` so the caller can flash + audio immediately. Wave state
// (`lines`, `currentLine`, `progress`, `currentToteId`) is reactive
// and re-renders the caller as scans land.
//
// Design contract:
//   - processScan never throws and never awaits. The result is
//     authoritative for "did we accept this barcode locally?".
//   - The sync queue is fire-and-forget. Network errors retry with
//     backoff; persistent BE rejections trigger a reconcile.
//   - Reconciliation refetches /picking/active and merges in the
//     unsynced scans on top of fresh state.
//   - The hook does NOT compute lookahead/progress separately —
//     derived state is recomputed off `lines` on demand by the caller.

import { useCallback, useEffect, useRef, useState } from "react";
import { apiFetch } from "../api/apiFetch";
import { API_BASE } from "../api/base";
import { recoverScanCaseReported } from "../components/helpers/scanCase";
import type {
    ActiveWaveHeader,
    ActiveWaveLine,
    ActiveWaveProgress,
    ActiveWaveResponse,
} from "./useActiveWave";

// ─── Public types ─────────────────────────────────────────────────

export type ScanRejectReason =
    | "wrong_item"
    | "wrong_location"
    | "scan_tote_first"
    | "scan_location_first"
    | "scan_starting_location_first"
    | "invalid_starting_location"
    // Starting-location barcode is a live location in ANOTHER
    // warehouse — the sync-reject extras carry `locationWarehouse`.
    | "wrong_warehouse"
    | "unknown_tote"
    | "unknown_barcode"
    | "line_already_complete"
    | "wave_complete"
    | "tote_unavailable"
    | "tote_in_use_elsewhere"
    // Pick to tote (queue 4): one tote per order — a second tote scan is
    // refused once the first holds items.
    | "single_tote_order"
    // Pick & Sort picks into york cages only: a box / pallet tote is
    // refused whatever its status. Any number of yorks per wave.
    | "tote_not_york"
    // Singles: a tote swap would split the order the picker is in the
    // middle of across two yorks. The reject carries `partPicked` (what
    // is left to finish it); the host shows a card and re-posts the
    // swap with `force` if the picker chooses "Swap anyway".
    | "order_part_picked"
    // BE auto-released the wave because no eligible order could be
    // appended (e.g. a Large pool drained between start and the first
    // tote scan). The host treats this as "no orders — exit", not an error.
    | "wave_empty_auto_released"
    // Resumed wave awaiting its pause-time tote re-scan; `expected`
    // carries the tote id.
    | "scan_resume_tote_first"
    | "needs_reconciliation";

export type ScanAccepted = {
    kind: "accepted";
    type: "location" | "item" | "tote" | "starting_location";
    lineId?: number;
    qtyPicked?: number;
    qtyRequired?: number;
    lineComplete?: boolean;
    // With lineComplete: true when no other open line for the same
    // (sku, location) remains — i.e. the hero's combined counter is
    // genuinely done, not just one batch-line of a split SKU. The
    // celebratory "line done" cue keys off this, not lineComplete:
    // playing it after batch 1 of 2 told pickers "move on" while the
    // hero still wanted more from the same shelf (2026-10-06, wave
    // 10112 / I54L1 + I64L3: "it sent me back").
    groupComplete?: boolean;
    waveComplete?: boolean;
    // Large only: the current order finished but the wave continues —
    // the picker must scan a fresh tote for the next order.
    orderComplete?: boolean;
    awaitingFinalTote?: boolean;
    // True when the accepted barcode triggered an optimistic UI change
    // (qty++, status flip). False for idempotent rescans (e.g., rescan
    // the same tote, rescan the same location after it's already in
    // 'picking'). Lets the caller decide whether to play a louder cue.
    stateChanged: boolean;
};

export type ScanRejected = {
    kind: "rejected";
    reason: ScanRejectReason;
    expected?: string;
};

// order_part_picked payload: the order the picker is part-way through,
// and every line of it still to pick. Mirrors the BE's
// partPickedRejectBody (picking.controller.js).
export type PartPickedLine = {
    line_id: number;
    sku: string;
    location: string;
    location_display: string;
    product_title: string | null;
    qty_remaining: number;
};

export type PartPickedOrder = {
    orderId: string;
    /** The tote the order's picked units are already in. */
    toteId: string;
    qtyPicked: number;
    qtyRequired: number;
    remaining: PartPickedLine[];
};

export type ScanResult = ScanAccepted | ScanRejected;

export type PickingSession = {
    wave: ActiveWaveHeader;
    lines: ActiveWaveLine[];
    currentLine: ActiveWaveLine | null;
    progress: ActiveWaveProgress;
    currentToteId: string | null;
    waveVersion: number;
    syncBacklog: number;          // pending POSTs in flight + queued
    syncError: boolean;            // some scan hasn't synced and we've given up retrying for now
    isReconciling: boolean;
    processScan: (barcode: string) => ScanResult;
    reconcile: () => Promise<void>;
};

// ─── Internal types ───────────────────────────────────────────────

type ScanKind = "scan-location" | "scan-item" | "scan-tote";

type QueuedCommand = {
    seq: number;
    // Idempotency key sent to the BE as client_scan_id. Retries after a
    // network error re-send the SAME id, so a timeout where the server
    // actually applied the scan can never double-count a pick — the BE
    // dedups on (wave_id, client_scan_id) and acks the re-delivery.
    id: string;
    kind: ScanKind;
    barcode: string;
    clientTs: number;
    attempts: number;
    // Snapshot of which line was current at scan time; used by the
    // reconciliation path to detect "the scan that landed targeted a
    // different line than the one we ended up writing against."
    targetLineId: number | null;
    // Tote / starting-location scans accept optimistically for state but
    // can't be validated locally, so their success/error cue waits for
    // the BE. When true, a confirmed sync fires onSyncAccept.
    deferAudio: boolean;
    // Replayed from the durable store after a reload — the picker acked
    // this scan (possibly minutes ago, possibly on a previous page
    // load), so its late settlement must not fire cues. Reconciliation
    // still runs on a rejection; only the audio/toast callbacks are
    // suppressed.
    silent?: boolean;
};

// ─── Durable sync queue (survives refresh / offline) ──────────────
//
// The in-memory queue used to be the only record of unsynced scans:
// three quick retries (~6s) and the command was dropped, and a page
// refresh discarded the lot. On 2026-08-19 (wave 5734) a picker worked
// through a wifi blackspot for 13 minutes — every pick beeped green,
// none were recorded, and the resumed wave walked her back four
// locations to re-scan items already in her tote. Every unsettled
// command is therefore mirrored to localStorage keyed by wave, replayed
// on remount, and retried indefinitely. Re-sends are safe by
// construction: the BE dedups scan-item on (wave_id, client_scan_id)
// and the other kinds are naturally idempotent.

export type PersistedScanCommand = {
    id: string;
    kind: ScanKind;
    barcode: string;
    clientTs: number;
    targetLineId: number | null;
};

const PERSIST_PREFIX = "picking:unsynced:";
// A queue nobody replayed inside two days belongs to a wave that was
// force-released or completed without this device — replaying it would
// be wrong, so it's garbage-collected instead.
const PERSIST_TTL_MS = 48 * 60 * 60 * 1000;

export const persistedQueueKey = (waveId: number): string => `${PERSIST_PREFIX}${waveId}`;

export const writePersistedQueue = (waveId: number, cmds: PersistedScanCommand[]): void => {
    try {
        const key = persistedQueueKey(waveId);
        if (cmds.length === 0) window.localStorage.removeItem(key);
        else window.localStorage.setItem(key, JSON.stringify({ ts: Date.now(), cmds }));
    } catch { /* storage full/disabled — in-memory queue still works */ }
};

export const readPersistedQueue = (waveId: number): PersistedScanCommand[] => {
    try {
        const raw = window.localStorage.getItem(persistedQueueKey(waveId));
        if (!raw) return [];
        const parsed = JSON.parse(raw) as { ts?: number; cmds?: PersistedScanCommand[] };
        if (typeof parsed?.ts !== "number" || Date.now() - parsed.ts > PERSIST_TTL_MS) {
            window.localStorage.removeItem(persistedQueueKey(waveId));
            return [];
        }
        return (Array.isArray(parsed.cmds) ? parsed.cmds : []).filter(c =>
            c && typeof c.id === "string" && typeof c.barcode === "string"
            && (c.kind === "scan-location" || c.kind === "scan-item" || c.kind === "scan-tote"));
    } catch {
        return [];
    }
};

// Drop expired/corrupt queues from OTHER waves — this device may have
// abandoned a wave without ever remounting its hook.
export const gcStalePersistedQueues = (): void => {
    try {
        const stale: string[] = [];
        for (let i = 0; i < window.localStorage.length; i++) {
            const key = window.localStorage.key(i);
            if (!key || !key.startsWith(PERSIST_PREFIX)) continue;
            try {
                const parsed = JSON.parse(window.localStorage.getItem(key) ?? "");
                if (typeof parsed?.ts !== "number" || Date.now() - parsed.ts > PERSIST_TTL_MS) {
                    stale.push(key);
                }
            } catch {
                stale.push(key);
            }
        }
        for (const key of stale) window.localStorage.removeItem(key);
    } catch { /* ignore */ }
};

// ─── Pure helpers (extracted for testability) ─────────────────────

export const findCurrentLine = (lines: ActiveWaveLine[]): ActiveWaveLine | null => {
    let best: ActiveWaveLine | null = null;
    for (const l of lines) {
        if (l.status === "pending" || l.status === "picking") {
            if (!best || l.sort_index < best.sort_index
                || (l.sort_index === best.sort_index && l.line_id < best.line_id)) {
                best = l;
            }
        }
    }
    return best;
};

// Recompute the hero's combined group quantities for `line` from the
// LIVE line set. Local-first mutates `lines` optimistically as picks
// land, so the group fields the BE baked in at bootstrap go stale once
// a batch-sibling is (un)picked — recomputing here keeps the hero's
// single 0/10 counter accurate across the batch boundary without a
// round-trip. A group = every line sharing this line's (sku, location).
// group_qty_prior_picked excludes `line` itself so the caller renders
// `group_qty_prior_picked + line.qty_picked` and the optimistic qty++
// still drives the counter live.
export const groupTotalsFor = (
    lines: ActiveWaveLine[],
    line: ActiveWaveLine,
): { group_qty_required: number; group_qty_prior_picked: number } => {
    let req = 0;
    let picked = 0;
    for (const l of lines) {
        if (l.sku === line.sku && l.location === line.location) {
            req += l.qty_required;
            picked += l.qty_picked;
        }
    }
    return {
        group_qty_required: req,
        group_qty_prior_picked: Math.max(0, picked - line.qty_picked),
    };
};

// Recompute the "Also at this location" list for `line` from the LIVE
// line set, mirroring the BE's decorateLinesWithOthersAtLocation (open
// lines at the same location, host SKU excluded, same-SKU siblings
// merged). The BE bakes the list in at bootstrap, so once a sibling is
// picked locally it would keep showing on the card until the next
// server refetch — recomputing here drops it the moment its local
// status flips. Quantities are the REMAINING units (qty_required −
// qty_picked) so a partially-picked sibling shows what's actually left
// to grab; fully-picked entries drop out entirely.
export const othersAtLocationFor = (
    lines: ActiveWaveLine[],
    line: ActiveWaveLine,
): Array<{ sku: string; title: string | null; thumbnail_url: string | null; barcode: string | null; qty_required: number }> => {
    const bySku = new Map<string, { sku: string; title: string | null; thumbnail_url: string | null; barcode: string | null; qty_required: number }>();
    for (const other of lines) {
        if (other.line_id === line.line_id) continue;
        if (other.status !== "pending" && other.status !== "picking") continue;
        if (other.location !== line.location) continue;
        if (other.sku === line.sku) continue;
        const remaining = Math.max(0, other.qty_required - other.qty_picked);
        if (remaining === 0) continue;
        const existing = bySku.get(other.sku);
        if (existing) {
            existing.qty_required += remaining;
        } else {
            bySku.set(other.sku, {
                sku: other.sku,
                title: other.product_title ?? null,
                thumbnail_url: other.product_thumbnail_url ?? other.product_image_url ?? null,
                barcode: other.product_barcode ?? null,
                qty_required: remaining,
            });
        }
    }
    return [...bySku.values()];
};

// Recompute the location-level totals for `line`'s physical location
// from the LIVE line set — Σ qty_required / Σ qty_picked over EVERY
// wave line at that location, all SKUs, all statuses, plus the count
// of distinct SKUs in that set. Drives the "Units at this location"
// counter in the also-at-location panel, so it must move on every
// accepted pick at the location, including sibling-SKU picks
// (scan-any-item-at-location); the SKU count gates the counter's
// visibility (only shown when > 1). Missing/flagged lines stay in the
// denominator, matching the group counter's convention — the target
// never silently shrinks. The BE bakes the same fields in at
// bootstrap/scan responses; this recompute keeps the local-first path
// live between server refetches.
export const locationTotalsFor = (
    lines: ActiveWaveLine[],
    line: ActiveWaveLine,
): { location_qty_required: number; location_qty_picked: number; location_sku_count: number } => {
    let req = 0;
    let picked = 0;
    const skus = new Set<string>();
    for (const l of lines) {
        if (l.location !== line.location) continue;
        req += l.qty_required;
        picked += l.qty_picked;
        skus.add(l.sku);
    }
    return { location_qty_required: req, location_qty_picked: picked, location_sku_count: skus.size };
};

export const findPrevPickedLocation = (lines: ActiveWaveLine[]): string | null => {
    let best: ActiveWaveLine | null = null;
    for (const l of lines) {
        if (l.status === "picked") {
            if (!best || l.sort_index > best.sort_index
                || (l.sort_index === best.sort_index && l.line_id > best.line_id)) {
                best = l;
            }
        }
    }
    return best?.location ?? null;
};

export const computeProgress = (
    lines: ActiveWaveLine[],
    plannedTotal: number | null,
    // Large only: order to scope orderTotalItems/orderItemsPicked to.
    // The BE bakes these fields into scan responses, but local-first
    // recomputes progress from the live line set on every render —
    // without recomputing the order scope too, this overwrote the
    // BE's fields and the counter fell back to the wave-cumulative
    // numbers ("34/42" after the JIT append, reported 2026-08-19).
    scopeOrderId?: string | null,
): ActiveWaveProgress => {
    let itemsPicked = 0;
    let summed = 0;
    let orderPicked = 0;
    let orderTotal = 0;
    for (const l of lines) {
        itemsPicked += l.qty_picked;
        summed += l.qty_required;
        if (scopeOrderId != null && l.order_id === scopeOrderId) {
            orderPicked += l.qty_picked;
            orderTotal  += l.qty_required;
        }
    }
    const progress: ActiveWaveProgress = {
        itemsPicked,
        totalItems: plannedTotal != null ? plannedTotal : summed,
    };
    if (orderTotal > 0) {
        progress.orderTotalItems  = orderTotal;
        progress.orderItemsPicked = orderPicked;
    }
    return progress;
};

// Which order the Large counter should be scoped to: the order of the
// next unpicked line (the one the picker is on — JIT-appended lines
// sort after it, so a mid-pick append doesn't move the scope), or,
// between orders when every line is done, the most recently finished
// order — so the tote prompt reads "34/34", not the wave totals.
// Null when lines carry no order_id (older BE payload): the display
// then falls back to the wave-scoped counter, same as before.
export const orderScopeFor = (
    lines: ActiveWaveLine[],
    currentLine: ActiveWaveLine | null,
): string | null => {
    if (currentLine?.order_id != null) return currentLine.order_id;
    let best: ActiveWaveLine | null = null;
    for (const l of lines) {
        if (l.order_id == null) continue;
        if (!best || l.sort_index > best.sort_index
            || (l.sort_index === best.sort_index && l.line_id > best.line_id)) {
            best = l;
        }
    }
    return best?.order_id ?? null;
};

// ─── Validation core (pure, testable) ─────────────────────────────

type ValidationInput = {
    barcode: string;
    wave: ActiveWaveHeader;
    lines: ActiveWaveLine[];
    barcodeMap: Record<string, string[]>;
    currentToteId: string | null;
    plannedTotal: number | null;
};

type ValidationOutput =
    | { kind: "accepted"; cmd: ScanKind; result: ScanAccepted; nextLines: ActiveWaveLine[]; nextToteId: string | null }
    | { kind: "rejected"; result: ScanRejected };

// Pure: given the current session state + a barcode, decide whether to
// accept (and what state mutation to apply) or reject. The hook itself
// only handles the imperative bits — state setting, queue scheduling,
// reconciliation.
export const validateScan = (input: ValidationInput): ValidationOutput => {
    const { barcode, wave, lines, barcodeMap, currentToteId, plannedTotal: _planned } = input;

    // A "tote candidate" — a barcode we can't validate locally (not the
    // current line's location, not a known wave item). It might be a
    // real tote (open/swap) or just a bad scan; only the BE can tell, so
    // we accept it optimistically WITHOUT changing local state and let
    // registerToteScan resolve the specific tote's status per-scan. Audio
    // is deferred (type "tote"): the BE's onSyncAccept/onSyncReject drives
    // the single good/bad cue, so a non-tote bad scan never double-beeps.
    const toteCandidate = (): ValidationOutput => ({
        kind: "accepted",
        cmd: "scan-tote",
        result: { kind: "accepted", type: "tote", stateChanged: barcode !== currentToteId },
        nextLines: lines,
        nextToteId: currentToteId,
    });

    const isKnownItem = (barcodeMap[barcode]?.length ?? 0) > 0;

    // 0) Resume-tote verification. A freshly-resumed wave accepts ONLY
    //    the tote that was open at pause — the picker proves they hold
    //    the wave's partially-picked items before anything else
    //    registers. The matching scan goes through as a tote command
    //    (the BE clears the gate + acks the still-open tote); every
    //    other barcode is rejected with the expected tote so the host
    //    can keep prompting for it.
    if (wave.pending_resume_tote) {
        if (barcode === wave.pending_resume_tote) {
            return toteCandidate();
        }
        return {
            kind: "rejected",
            result: { kind: "rejected", reason: "scan_resume_tote_first", expected: wave.pending_resume_tote },
        };
    }

    // 1) Starting location? Large waves anchor the picker before any
    //    other scan. The BE looks up walk_order, persists, kicks
    //    ensureLookahead; we pass the scan through (the FE has no
    //    walk_order data) and reconcile once the BE responds. Single
    //    (light) is upfront-allocated, so it skips this anchor step.
    if (wave.pick_type === "large" && wave.starting_location === null) {
        return {
            kind: "accepted",
            cmd: "scan-location",
            result: {
                kind: "accepted",
                type: "starting_location",
                stateChanged: true,
            },
            nextLines: lines,
            nextToteId: currentToteId,
        };
    }

    // 2) No tote open → the picker must scan a tote. A known wave item
    //    here is the wrong action (scan a tote first); anything else is
    //    a tote candidate the BE validates.
    if (!currentToteId) {
        if (isKnownItem) {
            return { kind: "rejected", result: { kind: "rejected", reason: "scan_tote_first" } };
        }
        return toteCandidate();
    }

    const currentLine = findCurrentLine(lines);
    // 4) No current line → wave is locally complete. Caller routes to
    //    /complete which triggers the BE wave-wrap writes.
    if (!currentLine) {
        return { kind: "rejected", result: { kind: "rejected", reason: "wave_complete" } };
    }

    // 5) Location scan: barcode matches the current line's expected
    //    location. Flip pending → picking idempotently. Re-scanning a
    //    location that's already 'picking' is fine — stateChanged=false
    //    so the caller can opt out of audio.
    if (barcode === currentLine.location) {
        const nextLines = currentLine.status === "pending"
            ? lines.map(l => l.line_id === currentLine.line_id ? { ...l, status: "picking" as const } : l)
            : lines;
        return {
            kind: "accepted",
            cmd: "scan-location",
            result: {
                kind: "accepted",
                type: "location",
                lineId: currentLine.line_id,
                stateChanged: currentLine.status === "pending",
            },
            nextLines,
            nextToteId: currentToteId,
        };
    }

    // 6) Item scan: resolve via barcodeMap.
    const candidates = barcodeMap[barcode];
    if (!candidates || candidates.length === 0) {
        // Not the current location and not a known wave item. It could
        // be a tote the picker is swapping to mid-pick, or just a bad
        // scan — the FE can't tell without the registry, so hand it to
        // the BE as a tote candidate. registerToteScan accepts a real
        // tote (swap) or returns unknown_tote, which surfaces the X.
        return toteCandidate();
    }
    // Scan-any-item-at-location: the line this scan picks against.
    // Prefer the current line when the barcode resolves to its SKU;
    // otherwise the first open line at the SAME location whose SKU
    // matches — the picker sweeps every item at the active bay in
    // whatever order the shelf presents. The hero keeps showing the
    // current line (it's still open); the sibling's remaining qty
    // moves on the "Also at this location" card.
    //
    // Large is one tote per ORDER, and the wave carries the next JIT
    // order's lines as pending — a same-location sibling from a
    // DIFFERENT order would drop that order's unit into the current
    // order's tote (and could close the tote mid-order when the
    // sibling pick completes its order). So on Large the sibling must
    // share the current line's order — both order_ids present and
    // equal, so an older BE payload without order_id safely stays
    // strict. Shared-tote types (Single / P&S) sweep the whole
    // location. Mirrors the BE gate in scanItem/scanItemLocalFirst.
    let targetLine = currentLine;
    if (!candidates.includes(currentLine.sku)) {
        const alt = lines
            .filter(l =>
                (l.status === "pending" || l.status === "picking")
                && l.line_id !== currentLine.line_id
                && l.location === currentLine.location
                && l.qty_picked < l.qty_required
                && candidates.includes(l.sku)
                && (wave.pick_type !== "large"
                    || (!!l.order_id && l.order_id === currentLine.order_id)))
            .sort((a, b) => a.sort_index - b.sort_index || a.line_id - b.line_id)[0];
        if (!alt) {
            return { kind: "rejected", result: { kind: "rejected", reason: "wrong_item", expected: currentLine.sku } };
        }
        targetLine = alt;
    }

    // 7) Item scan validation continues: require_location_scan +
    //    same-bay auto-transition logic. The gate keys off the CURRENT
    //    line — its status carries "has the picker confirmed this
    //    location" — even when the pick lands on a sibling line.
    if (currentLine.status === "pending") {
        if (wave.require_location_scan) {
            const prevLoc = findPrevPickedLocation(lines);
            if (prevLoc !== currentLine.location) {
                return {
                    kind: "rejected",
                    result: { kind: "rejected", reason: "scan_location_first", expected: currentLine.location },
                };
            }
            // Same-bay as previous pick — auto-transition the line.
        }
        // require_location_scan=false OR same-location: implicit transition.
    }
    if (targetLine.qty_picked >= targetLine.qty_required) {
        return { kind: "rejected", result: { kind: "rejected", reason: "line_already_complete" } };
    }

    // 8) Apply the item write locally. A sibling pick also flips a
    //    still-pending current line to 'picking' (the picker is
    //    engaged at the location), mirroring the BE's same-location
    //    auto-transition so phase stays in awaiting-items.
    const newQty = targetLine.qty_picked + 1;
    const newStatus = newQty >= targetLine.qty_required ? "picked" as const : "picking" as const;
    const nextLines = lines.map(l => {
        if (l.line_id === targetLine.line_id) return { ...l, qty_picked: newQty, status: newStatus };
        if (l.line_id === currentLine.line_id
            && targetLine.line_id !== currentLine.line_id
            && l.status === "pending") {
            return { ...l, status: "picking" as const };
        }
        return l;
    });

    // Wave completion: every line picked + a tote is open.
    //
    // ALL pick types are locally decidable now. Pick & Sort and Light
    // (Single) are upfront-allocated at build. Large is ONE ORDER PER
    // WAVE (2026-08-19 rebuild): the wave arrives with its single
    // order's lines and nothing is ever appended afterwards, so the
    // local line set IS the whole wave — finishing the last line
    // finishes the wave, full stop. The BE's wave.completed SSE is now
    // just a redundant confirmation, NOT a dependency: the old
    // "suppress local waveComplete for Large and wait for SSE" gate
    // (a JIT-chaining artefact) left pickers stranded on a stale SCAN
    // TOTE prompt whenever the SSE stream was down (the 2026-08-19
    // split-brain storm) — never reintroduce it.
    // Keyed off the TARGET line: a sibling pick can never complete the
    // wave while the current line is still open (it fails this
    // every-other-picked check).
    const everyOtherPicked = lines.every(l =>
        l.line_id === targetLine.line_id || l.status === "picked"
        || l.status === "missing" || l.status === "flagged"
    );
    const lineComplete = newStatus === "picked";
    // Batch-sibling aware: the SKU-at-location group is done only when
    // no other open line shares the target's (sku, location).
    const groupComplete = lineComplete && !nextLines.some(l =>
        l.line_id !== targetLine.line_id
        && l.sku === targetLine.sku
        && l.location === targetLine.location
        && (l.status === "pending" || l.status === "picking"));
    // Paired wave (Alex, 2026-10-16): the local line set is only THIS
    // picker's half. When their lines run out the BE either completes
    // the half (its wave_complete / the wave.completed SSE finishes the
    // screen) or — nobody having joined the other half — folds that
    // half onto the end of this wave and pushes its lines down as
    // lines.appended. Either way the device must not celebrate here:
    // the BE's answer is the only one that knows which it is.
    const paired = (wave.segment_count ?? 1) > 1;
    const waveComplete = lineComplete && everyOtherPicked && !!currentToteId && !paired;
    // Retired with JIT chaining: a Large "order done but wave continues"
    // state no longer exists (order done = wave done). Kept in the
    // result shape as always-false so older consumers stay type-happy.
    const orderComplete = false;

    return {
        kind: "accepted",
        cmd: "scan-item",
        result: {
            kind: "accepted",
            type: "item",
            lineId: targetLine.line_id,
            qtyPicked: newQty,
            qtyRequired: targetLine.qty_required,
            lineComplete,
            groupComplete,
            waveComplete,
            orderComplete,
            awaitingFinalTote: false,
            stateChanged: true,
        },
        nextLines,
        nextToteId: currentToteId,
    };
};

// ─── The hook ─────────────────────────────────────────────────────

const RETRY_DELAYS_MS = [250, 1000, 5000];
const SYNC_ERROR_THRESHOLD = 3;
// After the fast per-command retries exhaust, the drainer re-probes the
// oldest unsettled command at this cadence — one request per pass, so a
// paused wave or dead network is poked, not spammed.
const FLAT_RETRY_MS = 10_000;

export type PickingSessionOptions = {
    // Fired when the BE rejects a scan we already accepted locally
    // (HTTP 200 + body.ok=false). The hook still triggers a reconcile;
    // this callback exists so the caller can surface a toast / play an
    // error cue, because the optimistic-accept path silently flashed
    // green and the picker otherwise gets no feedback that the scan
    // was actually rejected.
    onSyncReject?: (
        reason: ScanRejectReason,
        kind: ScanKind,
        barcode: string,
        // Reason-specific payload from the BE rejection body (e.g.
        // wrong_warehouse → which warehouse the location belongs to;
        // order_part_picked → the order and what is left of it).
        extras?: { locationWarehouse?: string; partPicked?: PartPickedOrder },
    ) => void;
    // Fired when the BE CONFIRMS a scan whose audio/flash was deferred —
    // i.e. tote and starting-location scans, which the FE accepts
    // optimistically for state but can't actually validate (tote
    // availability, walk-order). For these the host suppresses the
    // immediate success cue and plays it here instead, so the picker
    // never hears a positive beep that a BE rejection then contradicts.
    onSyncAccept?: (kind: ScanKind, barcode: string) => void;
    // Fired when a reconcile discovers the wave is no longer ACTIVE for
    // this picker — /picking/active came back empty because the wave
    // was paused (idle cron), force-released, or completed elsewhere.
    // Without this the picker keeps scanning into 403s with no feedback
    // (wave 5734, 2026-08-19: an auto-paused wave silently swallowed
    // two minutes of scans). The host should leave the scan screen and
    // refetch — unsynced commands stay persisted and replay when the
    // resumed wave remounts the hook.
    onWaveGone?: () => void;
    // Fired when the BE publishes a `wave.completed` SSE event — the
    // authoritative completion signal. For Pick & Sort the host's
    // inline scan handler usually fires the celebrate+navigate before
    // this lands (local detection is correct for Pick & Sort), so the
    // host should dedupe. For Single/Large this is the ONLY trigger —
    // the local hook suppresses waveComplete=true on scan-item because
    // the BE may JIT-append more orders. Idempotent — the host should
    // ignore repeat firings.
    onWaveCompleted?: () => void;
};

// Merge a /picking/active snapshot over the device's line set.
//
// The snapshot wins everywhere EXCEPT on `protectedLineIds` — lines
// touched by a command the server hasn't acknowledged, or by one accepted
// locally after the snapshot was requested. On those, local progress is
// kept where it is AHEAD of the snapshot (a higher qty_picked, or
// pending → picking), because the server either hasn't applied the scan
// yet or applied it after the snapshot was read. Local progress is never
// kept where the server is already level or ahead, so a double-delivered
// scan can't double-count, and lines outside the set still rewind to
// server truth (that is what a reject-triggered reconcile is for).
// Lines the snapshot no longer carries are dropped; lines it adds are
// kept as-is.
export const mergeReconciledLines = (
    fresh: ActiveWaveLine[],
    local: ActiveWaveLine[],
    protectedLineIds: Set<number>,
): ActiveWaveLine[] => {
    if (protectedLineIds.size === 0) return fresh;
    const localById = new Map(local.map(l => [l.line_id, l]));
    let changed = false;
    const merged = fresh.map((fl) => {
        if (!protectedLineIds.has(fl.line_id)) return fl;
        const ll = localById.get(fl.line_id);
        if (!ll) return fl;
        const localQty = Math.min(ll.qty_picked, fl.qty_required);
        const qtyAhead = localQty > fl.qty_picked;
        const engagedAhead = fl.status === "pending" && ll.status === "picking";
        if (!qtyAhead && !engagedAhead) return fl;
        changed = true;
        const qty_picked = qtyAhead ? localQty : fl.qty_picked;
        const status =
            qty_picked >= fl.qty_required ? "picked" as const :
            fl.status === "pending"       ? "picking" as const :
                                            fl.status;
        return { ...fl, qty_picked, status };
    });
    return changed ? merged : fresh;
};

export const usePickingSession = (initial: ActiveWaveResponse, options: PickingSessionOptions = {}): PickingSession => {
    if (!initial.wave) {
        throw new Error("usePickingSession requires a wave");
    }
    const initialWave = initial.wave;
    const onSyncRejectRef = useRef<PickingSessionOptions["onSyncReject"]>(options.onSyncReject);
    useEffect(() => { onSyncRejectRef.current = options.onSyncReject; }, [options.onSyncReject]);
    const onSyncAcceptRef = useRef<PickingSessionOptions["onSyncAccept"]>(options.onSyncAccept);
    useEffect(() => { onSyncAcceptRef.current = options.onSyncAccept; }, [options.onSyncAccept]);
    const onWaveCompletedRef = useRef<PickingSessionOptions["onWaveCompleted"]>(options.onWaveCompleted);
    useEffect(() => { onWaveCompletedRef.current = options.onWaveCompleted; }, [options.onWaveCompleted]);
    const onWaveGoneRef = useRef<PickingSessionOptions["onWaveGone"]>(options.onWaveGone);
    useEffect(() => { onWaveGoneRef.current = options.onWaveGone; }, [options.onWaveGone]);

    const [lines, setLines] = useState<ActiveWaveLine[]>(() => [...initial.lines]);
    const [currentToteId, setCurrentToteId] = useState<string | null>(initialWave.current_tote_id);
    const [waveVersion, setWaveVersion] = useState<number>(initialWave.wave_version);
    const [syncBacklog, setSyncBacklog] = useState<number>(0);
    const [syncError, setSyncError] = useState<boolean>(false);
    const [isReconciling, setIsReconciling] = useState<boolean>(false);
    // Tracked separately from waveRef because the parent re-derives
    // its `phase` based on `wave.starting_location` (the
    // awaiting-starting-location → awaiting-first-tote transition).
    // waveRef is a plain ref so mutating it doesn't trigger a
    // re-render; pairing it with this state means the parent
    // re-renders when the starting-location scan flips this to
    // non-null, the mirror useEffect re-runs, and the phase advances.
    const [startingLocation, setStartingLocation] = useState<string | null>(initialWave.starting_location);
    // Same ref+state pairing as startingLocation: the parent's phase
    // derivation needs a re-render when the resume-tote gate clears.
    const [, setPendingResumeTote] = useState<string | null>(initialWave.pending_resume_tote ?? null);

    // Lookups in refs so processScan reads fresh state without
    // depending on the latest closure. (React state setters take effect
    // on the next render — we need the synchronous-after-set view for
    // back-to-back scans.)
    const linesRef = useRef<ActiveWaveLine[]>(lines);
    const toteRef = useRef<string | null>(currentToteId);
    const versionRef = useRef<number>(waveVersion);
    const barcodeMapRef = useRef<Record<string, string[]>>(initial.barcodeMap ?? {});
    const waveRef = useRef<ActiveWaveHeader>(initialWave);
    const plannedTotalRef = useRef<number | null>(initial.progress?.totalItems ?? null);

    useEffect(() => { linesRef.current = lines; }, [lines]);
    useEffect(() => { toteRef.current = currentToteId; }, [currentToteId]);
    useEffect(() => { versionRef.current = waveVersion; }, [waveVersion]);

    const seqRef = useRef<number>(0);
    const queueRef = useRef<QueuedCommand[]>([]);
    const inFlightRef = useRef<Set<number>>(new Set());
    // Every command the server hasn't ACKNOWLEDGED yet (queued, in
    // flight, or parked awaiting retry), keyed by client_scan_id.
    // Mirrored to localStorage on every change so a refresh/crash can't
    // lose picks — see the durable-queue header above.
    const outstandingRef = useRef<Map<string, QueuedCommand>>(new Map());
    // Every command enqueued since the last reconcile's GET was issued,
    // acknowledged or not. reconcile() uses it to find scans that were
    // accepted locally while a snapshot was in flight (those may be
    // missing from the reply) and prunes it on each pass.
    const recentCommandsRef = useRef<QueuedCommand[]>([]);
    // Timer + latch for the flat retry drainer. flushRef breaks the
    // submitToBackend ↔ flushOutstanding definition cycle.
    const retryTimerRef = useRef<number | null>(null);
    const drainingRef = useRef(false);
    const flushRef = useRef<(() => Promise<void>) | null>(null);

    // ─── Sync queue ───────────────────────────────────────────────

    const persistOutstanding = useCallback((): void => {
        writePersistedQueue(
            waveRef.current.wave_id,
            [...outstandingRef.current.values()].map(({ id, kind, barcode, clientTs, targetLineId }) =>
                ({ id, kind, barcode, clientTs, targetLineId })),
        );
    }, []);

    // Server acknowledged the command (accepted OR actively rejected) —
    // either way it's settled and leaves the durable store.
    const ackCommand = useCallback((id: string): void => {
        if (outstandingRef.current.delete(id)) persistOutstanding();
    }, [persistOutstanding]);

    const armRetryTimer = useCallback((): void => {
        if (retryTimerRef.current != null) return;
        if (outstandingRef.current.size === 0) return;
        retryTimerRef.current = window.setTimeout(() => {
            retryTimerRef.current = null;
            flushRef.current?.().catch(() => {});
        }, FLAT_RETRY_MS);
    }, []);

    const submitToBackend = useCallback(async (cmd: QueuedCommand): Promise<void> => {
        // The flat drainer, the per-command backoff timers and the
        // 'online' listener can all race to the same command: never
        // double-send one that's on the wire, never re-send one the
        // server already settled.
        if (!outstandingRef.current.has(cmd.id)) return;
        if (inFlightRef.current.has(cmd.seq)) return;
        inFlightRef.current.add(cmd.seq);
        setSyncBacklog(outstandingRef.current.size);
        const endpoint =
            cmd.kind === "scan-location" ? `/picking/wave/${waveRef.current.wave_id}/scan-location` :
            cmd.kind === "scan-item"     ? `/picking/wave/${waveRef.current.wave_id}/scan-item` :
                                           `/picking/wave/${waveRef.current.wave_id}/scan-tote`;
        try {
            const res = await apiFetch(endpoint, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                // client_scan_id makes retries idempotent server-side
                // (scan-item dedups on it; the other endpoints are
                // naturally idempotent and ignore it).
                body: JSON.stringify({ barcode: cmd.barcode, client_scan_id: cmd.id }),
            });
            inFlightRef.current.delete(cmd.seq);
            if (!res.ok) {
                // 4xx/5xx — the request reached the BE but was refused
                // (403 paused/reassigned wave, 401 dead session, 500).
                // The scan did NOT apply, so the command keeps its place
                // in the durable store — dropping it here is exactly how
                // an auto-paused wave used to swallow picks silently
                // (wave 5734, 2026-08-19). The flat drainer re-probes
                // until the wave/session is healthy; re-sends are safe
                // via client_scan_id. Reconcile still runs so a
                // paused/gone wave surfaces via onWaveGone.
                console.warn(`[picking-sync] ${cmd.kind} ${cmd.barcode} → HTTP ${res.status}, kept for retry`);
                setSyncError(true);
                armRetryTimer();
                await scheduleReconcile();
                setSyncBacklog(outstandingRef.current.size);
                return;
            }
            ackCommand(cmd.id);
            const body: {
                ok: boolean; reason?: string; locationWarehouse?: string;
                wave_version?: number; currentToteId?: string;
                wave_complete?: boolean; waveComplete?: boolean;
                // order_part_picked fields (see PartPickedOrder).
                orderId?: string; toteId?: string; qtyPicked?: number; qtyRequired?: number;
                remaining?: PartPickedLine[];
            } = await res.json().catch(() => ({ ok: true }));
            if (body.wave_version != null) {
                const v = Number(body.wave_version);
                if (v > versionRef.current) {
                    setWaveVersion(v);
                    versionRef.current = v;
                    // BE wave_version moved forward — refetch to pick
                    // up any JIT appends or structural changes.
                    await scheduleReconcile();
                }
            }
            // Paired wave: the device never decides completion locally
            // (see validateScan), so the BE's word in the scan response is
            // honoured here as well as over SSE — the stream can be down.
            // The screen's scheduler is ref-guarded, so both firing is fine.
            if ((waveRef.current.segment_count ?? 1) > 1 && body.ok !== false
                && (body.wave_complete === true || body.waveComplete === true)) {
                try { onWaveCompletedRef.current?.(); }
                catch (e) { console.error("[picking-sync] onWaveCompleted threw:", e); }
            }
            // Apply the BE-confirmed tote on a scan-tote success. The
            // hook deliberately doesn't update currentToteId in
            // validateScan (the FE can't tell available vs closed vs
            // in-use-elsewhere from the registry alone), so this is
            // where the picker's UI advances from "scan tote" to
            // "scan items". On a reject (body.ok === false) the
            // currentToteId stays at its prior value and the picker
            // sees the rejection toast without losing their place.
            if (cmd.kind === "scan-tote" && body.ok !== false && typeof body.currentToteId === "string") {
                if (body.currentToteId !== toteRef.current) {
                    toteRef.current = body.currentToteId;
                    setCurrentToteId(body.currentToteId);
                }
            }
            // SSE-independent recovery. A confirmed location/tote scan
            // with no pickable line left locally means the BE holds (or
            // is about to JIT-append) lines this device doesn't have —
            // the Large anchor scan and the between-orders tote scan
            // both land here. Those lines normally arrive via the SSE
            // lines.appended push, but a lost stream must not strand
            // the wave (2026-08-19: pickers pinned on SCAN LOCATION
            // through 20+ abandoned Large waves when pushes stopped
            // arriving after a deploy). The tote case is the reliable
            // one — the BE tote handler waits for the async append —
            // so a reconcile here always recovers by the tote scan at
            // the latest.
            if (body.ok !== false
                && (cmd.kind === "scan-location" || cmd.kind === "scan-tote")
                && findCurrentLine(linesRef.current) === null) {
                await scheduleReconcile();
            }
            if (body.ok === false) {
                // BE rejected what we accepted locally. Reconcile.
                console.warn(`[picking-sync] ${cmd.kind} ${cmd.barcode} → BE reject (${body.reason}), reconciling`);
                // Replayed commands stay mute: the picker acked this scan
                // on a previous page load — a toast about it now, mid-walk,
                // would be noise about a scan they don't remember. The
                // reconcile below still trues up state.
                const cb = cmd.silent ? undefined : onSyncRejectRef.current;
                if (cb) {
                    try {
                        const partPicked: PartPickedOrder | undefined =
                            body.reason === "order_part_picked" && typeof body.orderId === "string"
                                ? {
                                    orderId: body.orderId,
                                    toteId: body.toteId ?? "",
                                    qtyPicked: Number(body.qtyPicked ?? 0),
                                    qtyRequired: Number(body.qtyRequired ?? 0),
                                    remaining: Array.isArray(body.remaining) ? body.remaining : [],
                                }
                                : undefined;
                        cb(
                            (body.reason as ScanRejectReason) ?? "needs_reconciliation",
                            cmd.kind,
                            cmd.barcode,
                            { locationWarehouse: body.locationWarehouse, partPicked },
                        );
                    }
                    catch (e) { console.error("[picking-sync] onSyncReject threw:", e); }
                }
                await scheduleReconcile();
            } else if (cmd.deferAudio && !cmd.silent) {
                // Confirmed a scan whose cue we held back (tote /
                // starting-location). Fire the success cue now — this is
                // the first positive feedback the picker gets for it.
                const cb = onSyncAcceptRef.current;
                if (cb) {
                    try { cb(cmd.kind, cmd.barcode); }
                    catch (e) { console.error("[picking-sync] onSyncAccept threw:", e); }
                }
            }
            setSyncBacklog(outstandingRef.current.size);
            // Only an EMPTY store clears the error state — one scan
            // squeaking through while older ones are still parked must
            // not hide the "picks unsaved" banner.
            if (outstandingRef.current.size === 0) setSyncError(false);
        } catch (err) {
            // Network error — the request likely never left the device.
            inFlightRef.current.delete(cmd.seq);
            cmd.attempts += 1;
            if (cmd.attempts <= RETRY_DELAYS_MS.length) {
                const delay = RETRY_DELAYS_MS[cmd.attempts - 1];
                setTimeout(() => { submitToBackend(cmd).catch(() => {}); }, delay);
            } else {
                // Fast retries exhausted — the device is probably
                // offline. The command STAYS in the durable store (it
                // used to be dropped here, silently losing the pick);
                // the flat drainer and the 'online' listener own it now.
                console.error(`[picking-sync] ${cmd.kind} ${cmd.barcode} failed after retries:`, err);
                if (cmd.attempts >= SYNC_ERROR_THRESHOLD) setSyncError(true);
                armRetryTimer();
                await scheduleReconcile();
            }
            setSyncBacklog(outstandingRef.current.size);
        }
    }, []);

    const enqueueScan = useCallback((kind: ScanKind, barcode: string, targetLineId: number | null, deferAudio: boolean): void => {
        seqRef.current += 1;
        const cmd: QueuedCommand = {
            seq: seqRef.current,
            // crypto.randomUUID is available in every browser the PWA
            // targets; the fallback covers older webviews.
            id: typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
                ? crypto.randomUUID()
                : `${Date.now()}-${seqRef.current}-${Math.random().toString(36).slice(2)}`,
            kind, barcode, clientTs: Date.now(), attempts: 0, targetLineId, deferAudio,
        };
        queueRef.current.push(cmd);
        // Durable from the moment of acceptance — a refresh between the
        // beep and the ack can no longer lose the pick.
        outstandingRef.current.set(cmd.id, cmd);
        recentCommandsRef.current.push(cmd);
        persistOutstanding();
        setSyncBacklog(outstandingRef.current.size);
        // Drain immediately — pop from the queue, submit, repeat.
        // We don't serialise the queue's draining (each fires
        // independently) because the BE re-validates and the FE has
        // wave_version reconciliation as a backstop. If serialisation
        // turns out to matter (e.g., two scans on the same line race),
        // we can flip drain to sequential.
        const next = queueRef.current.shift();
        if (next) submitToBackend(next).catch(() => {});
    }, [submitToBackend, persistOutstanding]);

    // Flat retry drainer — re-sends unsettled commands oldest-first,
    // one at a time, stopping at the first that doesn't settle: if the
    // wave is still paused or the device still offline, one probe per
    // pass is enough. Anything settled here happened OUT of band from
    // the picker's live flow (replay after reload, wave resumed,
    // connection back), so a reconcile trues up local state afterwards.
    const flushOutstanding = useCallback(async (): Promise<void> => {
        if (drainingRef.current) return;
        drainingRef.current = true;
        let settledAny = false;
        try {
            for (;;) {
                const next = [...outstandingRef.current.values()]
                    .filter(c => !inFlightRef.current.has(c.seq))
                    .sort((a, b) => a.clientTs - b.clientTs || a.seq - b.seq)[0];
                if (!next) break;
                await submitToBackend(next);
                if (outstandingRef.current.has(next.id)) break;
                settledAny = true;
            }
        } finally {
            drainingRef.current = false;
        }
        if (settledAny) scheduleReconcile().catch(() => {});
        armRetryTimer();
    }, [submitToBackend, armRetryTimer]);
    useEffect(() => { flushRef.current = flushOutstanding; }, [flushOutstanding]);

    // Replay any commands persisted by a previous page load of THIS
    // wave (with their original client_scan_ids, so re-delivery of a
    // scan the server DID apply before the reload dedups server-side),
    // and flush whenever connectivity returns.
    useEffect(() => {
        if (!initialWave.picking_local_first_enabled) return;
        gcStalePersistedQueues();
        const persisted = readPersistedQueue(initialWave.wave_id);
        if (persisted.length > 0) {
            for (const p of persisted) {
                if (outstandingRef.current.has(p.id)) continue;
                seqRef.current += 1;
                outstandingRef.current.set(p.id, {
                    seq: seqRef.current, id: p.id, kind: p.kind, barcode: p.barcode,
                    clientTs: p.clientTs, attempts: 0,
                    targetLineId: p.targetLineId ?? null,
                    deferAudio: false, silent: true,
                });
            }
            setSyncBacklog(outstandingRef.current.size);
            flushRef.current?.().catch(() => {});
        }
        const onOnline = () => { flushRef.current?.().catch(() => {}); };
        window.addEventListener("online", onOnline);
        return () => {
            window.removeEventListener("online", onOnline);
            if (retryTimerRef.current != null) {
                window.clearTimeout(retryTimerRef.current);
                retryTimerRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialWave.wave_id, initialWave.picking_local_first_enabled]);

    // ─── Reconciliation ───────────────────────────────────────────

    const reconcileInFlightRef = useRef<Promise<void> | null>(null);

    const reconcile = useCallback(async (): Promise<void> => {
        if (reconcileInFlightRef.current) return reconcileInFlightRef.current;
        const p = (async () => {
            setIsReconciling(true);
            // Sequence watermark: any command enqueued after this point
            // was accepted locally while the GET was in flight, and the
            // reply may or may not include it (see the merge below).
            const seqAtRequest = seqRef.current;
            try {
                const res = await apiFetch("/picking/active", { method: "GET" });
                if (!res.ok) return;
                const fresh: ActiveWaveResponse = await res.json();
                if (!fresh.wave) {
                    // Wave no longer ACTIVE for this picker — paused by
                    // the idle cron, force-released, or completed. Tell
                    // the host so it leaves the scan screen instead of
                    // letting the picker keep scanning into silent 403s
                    // (wave 5734, 2026-08-19). Unsettled commands stay
                    // persisted — they replay when the resumed wave
                    // remounts this hook.
                    try { onWaveGoneRef.current?.(); }
                    catch (e) { console.error("[picking-sync] onWaveGone threw:", e); }
                    return;
                }
                // Replace local state with the BE truth — EXCEPT on lines
                // this device has moved past the snapshot. Two kinds of
                // local progress must survive a reconcile:
                //   • commands still unacknowledged (parked offline /
                //     retrying) — the BE hasn't applied them yet;
                //   • commands accepted locally AFTER this GET was issued
                //     — the snapshot may predate them. 2026-10-06, wave
                //     10105 / N27L3: a reject-triggered reconcile raced
                //     a scan that landed one second later; the stale
                //     reply overwrote the pick and pinned the hero on an
                //     already-picked line for 90 s ("it sent me back").
                // mergeReconciledLines keeps local progress only on
                // those lines; everything else takes the BE value, so a
                // rejected scan (whose own reconcile this usually is) is
                // still rewound. Unsynced commands stay in the queue and
                // retry against the new state as before.
                const protectedLineIds = new Set<number>();
                for (const c of outstandingRef.current.values()) {
                    if (c.targetLineId != null) protectedLineIds.add(c.targetLineId);
                }
                for (const c of recentCommandsRef.current) {
                    if (c.seq > seqAtRequest && c.targetLineId != null) protectedLineIds.add(c.targetLineId);
                }
                recentCommandsRef.current = recentCommandsRef.current.filter(c => c.seq > seqAtRequest);
                const mergedLines = mergeReconciledLines(fresh.lines, linesRef.current, protectedLineIds);
                waveRef.current = fresh.wave;
                barcodeMapRef.current = fresh.barcodeMap ?? barcodeMapRef.current;
                plannedTotalRef.current = fresh.progress?.totalItems ?? plannedTotalRef.current;
                setLines(mergedLines);
                linesRef.current = mergedLines;
                setCurrentToteId(fresh.wave.current_tote_id);
                toteRef.current = fresh.wave.current_tote_id;
                setWaveVersion(fresh.wave.wave_version);
                versionRef.current = fresh.wave.wave_version;
                setStartingLocation(fresh.wave.starting_location);
                setPendingResumeTote(fresh.wave.pending_resume_tote ?? null);
            } catch (err) {
                console.error("[picking-sync] reconcile failed:", err);
            } finally {
                setIsReconciling(false);
            }
        })();
        reconcileInFlightRef.current = p;
        try {
            await p;
        } finally {
            reconcileInFlightRef.current = null;
        }
    }, []);

    const scheduleReconcile = useCallback(async (): Promise<void> => {
        // Coalesce: if a reconcile is already in flight, return its
        // promise; otherwise kick one. The function doesn't await for
        // the caller (submitToBackend keeps moving) but the promise is
        // reused.
        return reconcile();
    }, [reconcile]);

    // ─── processScan ──────────────────────────────────────────────

    const processScan = useCallback((rawBarcode: string): ScanResult => {
        const scanned = String(rawBarcode ?? "").trim();
        if (!scanned) {
            return { kind: "rejected", reason: "unknown_barcode" };
        }
        if (isReconciling) {
            // Don't accept scans while we're rebuilding state.
            return { kind: "rejected", reason: "needs_reconciliation" };
        }

        // A scanner whose wedge lags SHIFT sends the right barcode with its
        // opening letters unshifted, and every comparison below (plus the
        // BE's) is exact — so the pick silently refuses and the picker
        // scans again. Recover against this wave's closed sets only: its
        // item barcodes, its line locations, and the resume-tote gate.
        // Tote IDs are deliberately absent — they're validated by the BE
        // against the registry, which is not a closed set we hold, so a
        // mis-cased tote scan still fails loudly rather than being guessed.
        // Recovery happens BEFORE validateScan so the corrected value is
        // what reaches enqueueScan and therefore the BE. See scanCase.ts.
        const barcode = recoverScanCaseReported(
            scanned,
            function* () {
                yield* Object.keys(barcodeMapRef.current);
                for (const l of linesRef.current) yield l.location;
                if (waveRef.current.pending_resume_tote) yield waveRef.current.pending_resume_tote;
            }(),
            "Picking",
        ).value;

        const verdict = validateScan({
            barcode,
            wave: waveRef.current,
            lines: linesRef.current,
            barcodeMap: barcodeMapRef.current,
            currentToteId: toteRef.current,
            plannedTotal: plannedTotalRef.current,
        });

        if (verdict.kind === "rejected") {
            // Self-heal: "wave_complete" here means the LOCAL line set has
            // nothing left — which mid-wave (Large between JIT orders) just
            // means our snapshot is stale relative to the BE's sync-appended
            // next order. Kick a reconcile so the fresh lines + tote state
            // land and the picker's next scan goes through; for genuinely
            // finished waves the host is already navigating away and the
            // extra refetch is harmless.
            if (verdict.result.reason === "wave_complete") {
                scheduleReconcile().catch(() => {});
            }
            return verdict.result;
        }

        // Apply optimistic state.
        if (verdict.nextLines !== linesRef.current) {
            linesRef.current = verdict.nextLines;
            setLines(verdict.nextLines);
        }
        if (verdict.nextToteId !== toteRef.current) {
            toteRef.current = verdict.nextToteId;
            setCurrentToteId(verdict.nextToteId);
        }
        // Starting-location scan — Light/Large only, fires once per
        // wave. Optimistically flip wave.starting_location locally so
        // the parent's mirror useEffect re-derives phase from
        // awaiting-starting-location → awaiting-first-tote without
        // waiting for the BE round-trip. Without this update the next
        // scan re-enters the starting-location branch of validateScan
        // and the BE rejects it as a duplicate — the picker sees a
        // spurious "Wrong Barcode" + red tick before the prompt
        // finally advances.
        if (verdict.result.kind === "accepted" && verdict.result.type === "starting_location") {
            waveRef.current = { ...waveRef.current, starting_location: barcode };
            setStartingLocation(barcode);
        }
        // Resume-tote verification cleared: the picker scanned the
        // pause-time tote. Optimistically drop the gate locally (the BE
        // clears its column in the same scan) so the very next scan
        // validates normally instead of re-tripping the gate while the
        // tote ack round-trips.
        if (verdict.result.kind === "accepted" && verdict.result.type === "tote"
            && waveRef.current.pending_resume_tote
            && barcode === waveRef.current.pending_resume_tote) {
            waveRef.current = { ...waveRef.current, pending_resume_tote: null };
            setPendingResumeTote(null);
        }

        // Enqueue background sync. Tote + starting-location scans can't
        // be validated locally, so we hold their audio/flash until the
        // BE confirms (deferAudio) — see onSyncAccept / onSyncReject.
        const deferAudio = verdict.result.kind === "accepted"
            && (verdict.result.type === "tote" || verdict.result.type === "starting_location");
        enqueueScan(verdict.cmd, barcode, verdict.result.lineId ?? null, deferAudio);

        return verdict.result;
    }, [enqueueScan, isReconciling, scheduleReconcile]);

    // ─── SSE: server-pushed structural events ─────────────────────
    //
    // The BE publishes `lines.appended` whenever a JIT order append
    // commits, and `wave.completed` / `wave.released` on terminal
    // events. EventSource is one-way HTTP, works through every proxy,
    // and reconnects automatically on transient network drops.
    //
    // Only opened when the warehouse has the flag on — otherwise the
    // hook mounts but its scan path is never used, so the SSE
    // connection would just sit there consuming a BE worker slot.
    useEffect(() => {
        if (!initialWave.picking_local_first_enabled) return;
        if (typeof window === "undefined" || typeof window.EventSource === "undefined") return;
        // Build the absolute URL so it works when FE + BE are on
        // different origins (Amplify ↔ Render). Same API_BASE apiFetch uses.
        const url = `${API_BASE}/picking/wave/${initialWave.wave_id}/events`;
        let es: EventSource | null = null;
        try {
            es = new EventSource(url, { withCredentials: true });
        } catch (err) {
            console.warn("[picking-sse] EventSource failed to open:", err);
            return;
        }

        const onLinesAppended = (ev: MessageEvent) => {
            try {
                const body = JSON.parse(ev.data || "{}");
                const newLines: ActiveWaveLine[] = Array.isArray(body.lines) ? body.lines : [];
                if (newLines.length === 0) return;
                // Merge — keep existing lines + append. JIT orders
                // arrive with fresh line_ids so duplicates shouldn't
                // happen, but dedupe defensively.
                const seen = new Set(linesRef.current.map(l => l.line_id));
                const merged = [...linesRef.current];
                for (const l of newLines) {
                    if (!seen.has(l.line_id)) merged.push(l);
                }
                merged.sort((a, b) => a.sort_index - b.sort_index || a.line_id - b.line_id);
                linesRef.current = merged;
                setLines(merged);
                if (body.wave_version != null) {
                    const v = Number(body.wave_version);
                    if (v > versionRef.current) {
                        versionRef.current = v;
                        setWaveVersion(v);
                    }
                }
            } catch (err) {
                console.warn("[picking-sse] lines.appended parse error:", err);
            }
        };
        const onWaveCompleted = (_ev: MessageEvent) => {
            // Authoritative wave-complete from the BE. Pick & Sort
            // detects this locally on the last scan and runs its own
            // celebrate+navigate path; for Single/Large the local
            // hook suppresses waveComplete=true on scan-item (because
            // JIT-append might still happen), so THIS event is what
            // tells the host the wave really wrapped.
            try { onWaveCompletedRef.current?.(); }
            catch (e) { console.error("[picking-sse] onWaveCompleted threw:", e); }
            scheduleReconcile().catch(() => {});
        };
        const onError = (_ev: Event) => {
            // EventSource auto-reconnects on transient errors. If the
            // session truly broke (auth lost, picker reassigned), the
            // server returns 403 and EventSource gives up — we'll
            // hear about it via a sync 409 instead.
        };

        es.addEventListener("lines.appended", onLinesAppended as unknown as EventListener);
        es.addEventListener("wave.completed", onWaveCompleted as unknown as EventListener);
        es.addEventListener("error", onError as unknown as EventListener);

        return () => {
            es?.close();
        };
    }, [initialWave.wave_id, initialWave.picking_local_first_enabled, scheduleReconcile]);

    // ─── Derived state ────────────────────────────────────────────

    const currentLine = findCurrentLine(lines);
    const progress = computeProgress(
        lines,
        plannedTotalRef.current,
        waveRef.current.pick_type === "large" ? orderScopeFor(lines, currentLine) : null,
    );

    return {
        wave: waveRef.current,
        lines,
        currentLine,
        progress,
        currentToteId,
        waveVersion,
        syncBacklog,
        syncError,
        isReconciling,
        processScan,
        reconcile,
    };
};
